import { useRef } from "react";
import Link from "next/link";

import Alert from "ui-components/Alert";

type SubmitStatus = "not-started" | "submitting" | "complete";

interface Props {
  resetPasswordLink?: string;
  errorMessage: string | undefined | null;
  setErrorMessage: (error: string) => any;
  submitStatus: SubmitStatus | null | undefined;
  setSubmitStatus: (status: SubmitStatus) => any;
  createSuccess: boolean;
  setCreateSuccess: (status: boolean) => any;
  resetSubmitStatus: () => any;
  onSubmit: (email: string, password: string) => any;
}

const EmailLoginForm = ({
  resetPasswordLink,
  createSuccess,
  setCreateSuccess,
  submitStatus,
  setSubmitStatus,
  errorMessage,
  setErrorMessage,
  resetSubmitStatus,
  onSubmit,
}: Props) => {
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();

        const email = emailRef.current?.value;
        const password = passwordRef.current?.value;

        if (!email || !password) {
          setErrorMessage("Email and Password are BOTH MANDATORY!!!");
          return;
        }
        setSubmitStatus("submitting");
        const resp = await onSubmit(email, password);
        if (resp.success) {
          setCreateSuccess(true);
        } else {
          setCreateSuccess(false);
          setErrorMessage(resp.message);
        }
        setSubmitStatus("complete");
      }}
    >
      <div className="flex flex-wrap -mx-3 mb-4">
        <div className="w-full px-3">
          <label
            className="block text-gray-800 text-sm font-medium mb-1"
            htmlFor="email"
          >
            Email
          </label>
          <input
            ref={emailRef}
            onChange={resetSubmitStatus}
            id="email"
            type="email"
            className="form-input w-full text-gray-800 rounded"
            placeholder="Enter your email address"
            autoComplete="email"
            required
          />
        </div>
      </div>
      <div className="flex flex-wrap -mx-3 mb-4">
        <div className="w-full px-3">
          <div className="flex justify-between">
            <label
              className="block text-gray-800 text-sm font-medium mb-1"
              htmlFor="password"
            >
              Password
            </label>
            {resetPasswordLink && (
              <Link href={resetPasswordLink}>
                <a className="text-sm font-medium text-blue-600 hover:underline">
                  Having trouble signing in?
                </a>
              </Link>
            )}
          </div>
          <input
            ref={passwordRef}
            onChange={resetSubmitStatus}
            id="password"
            type="password"
            className="form-input w-full text-gray-800 rounded"
            placeholder="Enter your password"
            autoComplete="current-password"
            required
          />
        </div>
      </div>

      {/* Error Alert */}
      {submitStatus === "complete" && createSuccess === false && (
        <Alert>{errorMessage}</Alert>
      )}
      <div className="flex flex-wrap -mx-3 mt-6">
        <div className="w-full px-3">
          {submitStatus === "submitting" ? (
            <button className="btn text-white bg-blue-600 w-full" disabled>
              <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Submitting
            </button>
          ) : (
            <button
              className={`btn text-white bg-blue-600 w-full ${
                submitStatus === "not-started" ? "hover:bg-blue-700" : ""
              }`}
              type="submit"
              disabled={submitStatus !== "not-started"}
            >
              Sign In
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default EmailLoginForm;
